import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  DsBoxModule,
  DsButtonModule,
  DsCheckboxModule,
  DsFormFieldModule,
  DsLabelModule,
  DsMessageModule,
  DsModalModule,
  DsToastService,
  DsTooltipDirectiveModule,
} from '@bmw-ds/components';

import { RetailCrmService } from '../../../customer/clients/retail-crm/retail-crm.service';
import { CustomerVehicle, CustomerVehicleRelationType } from '../../../customer/model/customer-vehicle.model';
import { useI18n } from '../../../i18n/services/i18n.service';
import { ButtonRowComponent } from '../../../modal/button/button-row.component';
import { usePagination } from '../../../utils/pagination/pagination.provider';
import { AddRelationModalContainer } from './add-relation-modal.container';
import { AddVehicle } from './vehicles.view-model';

@Component({
  selector: 'cdf-add-vehicle-modal',
  imports: [
    DsButtonModule,
    DsCheckboxModule,
    DsLabelModule,
    FormsModule,
    DsBoxModule,
    DsModalModule,
    DsFormFieldModule,
    DsTooltipDirectiveModule,
    DsMessageModule,
    ButtonRowComponent,
    AddRelationModalContainer,
  ],
  host: { class: 'd-flex flex-column gap-3x' },
  template: `
    @switch (pagination.currentName()) {
      @case ('vehicle') {
        <p class="text-regular">{{ i18n('vehicles.addVehicleModal.enterVinPrompt') }}</p>
        <ds-form-field>
          <label ds-label for="cdf-add-vehicle-modal-vin"> {{ i18n('placeholder.vin') }} </label>

          <input
            ds-input
            id="cdf-add-vehicle-modal-vin"
            ds-form-validation
            required
            [(ngModel)]="vinField"
            [pattern]="vinFieldPattern"
            name="vin"
          />
          @if (!validVin()) {
            <ds-error>
              {{ i18n('vehicles.addVehicleModal.invalidVin') }}
            </ds-error>
          }
        </ds-form-field>
        <div class="d-flex justify-content-end gap-4x">
          <button ds-button id="cancel" variant="outline" (click)="closeModal.emit()">
            {{ i18n('button.cancel') }}
          </button>
          <button
            ds-button
            ds-loading
            [isLoading]="vehicleLoading()"
            [disabled]="!validVin()"
            [ds-tooltip]="i18n('vehicles.addVehicleModal.invalidVinTooltip')"
            [dsTooltipConfig]="{ disabled: validVin() }"
            (click)="loadVehicle(vinField()!)"
          >
            {{ i18n('button.continue') }}
          </button>
        </div>
      }
      @case ('relations') {
        @if (vehicle(); as vehicle) {
          <cdf-add-relation-modal-container
            [(selectedRelations)]="relations"
            [vehicle]="vehicle"
            [allCustomersOverride]="vehicle.allCustomers"
          />
        }

        <cdf-button-row>
          <button ds-button ds-loading id="cancel" variant="outline" (click)="pagination.back()">
            {{ i18n('button.back') }}
          </button>
          <button
            ds-button
            ds-loading
            [isLoading]="loading()"
            [disabled]="!vehicle() || relations().size === 0"
            (click)="pagination.next()"
          >
            {{ i18n('vehicles.addVehicleModal.confirmButton') }}
          </button>
        </cdf-button-row>
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddVehicleModalContainer {
  protected readonly i18n = useI18n();

  private readonly retailCrmService = inject(RetailCrmService);
  private readonly dsToastService = inject(DsToastService);

  protected readonly pagination = usePagination(['vehicle', 'relations'], () => {
    const vehicle = this.vehicle();
    const relations = this.relations();
    if (!vehicle || !relations.size) {
      throw new Error('VIN and at least one relation must be selected to add a vehicle');
    }
    this.addVehicle.emit({ vehicle: vehicle, relations });
  });

  readonly loading = input(false, { transform: booleanAttribute });
  readonly accountNumber = input.required<string>();

  protected readonly vinFieldPattern = /^[A-HJ-NPR-Z0-9]{17}$/i;

  readonly vinField = signal<string | undefined>(undefined);
  readonly validVin = computed(
    () => this.vinField()?.length === 17 && this.vinFieldPattern.test(this.vinField() ?? ''),
  );

  private readonly toastId = 'cdf-add-vehicle-modal-toast';

  protected readonly vehicle = signal<CustomerVehicle | undefined>(undefined);
  protected readonly vehicleLoading = signal(false);

  protected async loadVehicle(vin: string) {
    this.vehicleLoading.set(true);
    this.vehicle.set(undefined);
    this.dsToastService.popToast(this.toastId);
    try {
      const vehicle = await this.retailCrmService.getVehicle(vin);
      if (vehicle == null) {
        this.dsToastService.pushToast({
          id: this.toastId,
          tone: 'critical',
          toastTitle: this.i18n('vehicles.addVehicleModal.toastNotFoundText'),
          toastText: this.i18n('vehicles.addVehicleModal.toastNotFoundDescription'),
        });
      } else {
        const allCustomers = vehicle.allCustomers ?? {};
        const relationTypes = Object.values(CustomerVehicleRelationType).filter(
          (r) => allCustomers[r]?.accountNumber === this.accountNumber(),
        );
        this.vehicle.set({ ...vehicle, relationTypes });
        this.pagination.next();
      }
    } catch {
      this.dsToastService.pushToast({
        id: this.toastId,
        tone: 'critical',
        toastTitle: this.i18n('vehicles.addVehicleModal.toastLoadErrorText'),
        toastText: this.i18n('vehicles.addVehicleModal.toastLoadErrorDescription'),
      });
    } finally {
      this.vehicleLoading.set(false);
    }
  }

  protected readonly relations = linkedSignal<ReadonlySet<CustomerVehicleRelationType>>(() => {
    this.vehicle(); // Reset relations when VIN changes
    return new Set();
  });

  readonly addVehicle = output<AddVehicle>();
  readonly closeModal = output();
}
